import { Trans } from 'react-i18next';

import { Stack } from '@hotjar/hotsauce';
import type { CurrencySymbols } from '@hotjar/webapp/domains/common/settings/types';
import type { ValueOf } from 'type-fest';
import type { BillingPeriod } from '@hotjar/subscription-management';

import { prettifyPriceWithCurrencySymbol } from '@hotjar/webapp/billing/cost';
import { PlanPriceStyles as S } from './styles';
import type { GetCostResult } from '../../../PlanComparisonModal/hooks/useProductCost';

interface Props {
  price: GetCostResult;
  symbol: ValueOf<CurrencySymbols>;
  billingPeriod?: BillingPeriod;
}

export const PlanPrice = ({ price, symbol, billingPeriod }: Props): JSX.Element => {
  if (billingPeriod === 'ANNUALLY' && price.withDiscounts.gt(0) && price.withoutDiscounts.gt(0)) {
    const prettyPriceWithDiscounts = prettifyPriceWithCurrencySymbol(
      undefined,
      0,
      1,
    )(price.withDiscounts.dividedBy(12), 1);
    const prettyPriceWithoutDiscounts = prettifyPriceWithCurrencySymbol(
      symbol,
      0,
      1,
    )(price.withoutDiscounts.dividedBy(12), 1);

    return (
      <Stack direction="column" spacing="x0" style={{ gap: 0 }}>
        <S.PriceWrapper direction="row" spacing="x0">
          <S.StrikethroughPrice>{prettyPriceWithoutDiscounts}</S.StrikethroughPrice>
          <S.DiscountedPrice>
            <Trans
              i18nKey="billing:upsell.plan-trial.per-month"
              components={[<S.PriceValue textType="header5" as="h2" />]}
              values={{ symbol, price: prettyPriceWithDiscounts }}
            />
          </S.DiscountedPrice>
        </S.PriceWrapper>
        <S.BilledAnnuallyLabel>Billed annually</S.BilledAnnuallyLabel>
      </Stack>
    );
  }

  const prettyPriceWithoutDiscounts = prettifyPriceWithCurrencySymbol(
    undefined,
    0,
    0,
  )(price.withoutDiscounts, 0);

  return (
    <S.Price data-testid="plan-comparison-plan-price">
      <Trans
        i18nKey={
          price.withoutDiscounts.lessThanOrEqualTo(0)
            ? 'billing:upsell.plan-trial.forever'
            : 'billing:upsell.plan-trial.per-month'
        }
        components={[<S.PriceValue textType="header5" as="h2" />]}
        values={{ symbol, price: prettyPriceWithoutDiscounts }}
      />
    </S.Price>
  );
};
